import { Vector3 } from 'three';

import type { CameraManager } from '../camera-manager.class';
import type { CameraOffset, FramingOptions, SceneDimensions } from '../types';

export interface SetOptimalDistanceParams {
	dimensions: SceneDimensions;
	zoom: number;
	offset?: CameraOffset;
	framing?: FramingOptions;
}

// Extra breathing room so the framed object does not sit edge-to-edge.
const FRAMING_MARGIN = 1.3;

/**
 * Set optimal camera distance based on scene dimensions
 */
export const setOptimalDistanceCfn = (
	self: CameraManager,
	{ dimensions, zoom, offset, framing }: SetOptimalDistanceParams
): void => {
	const { width, depth, height } = dimensions;
	const camera = self.camera;
	const controls = self.controls;

	// Calculate optimal distance from the camera FOV *and* the viewport aspect
	// ratio. camera.fov is the vertical FOV; the horizontal FOV widens with the
	// aspect ratio. Fitting a wide object to the vertical FOV alone (ignoring
	// viewport width) pushes the camera too far back on landscape canvases.
	const fovVertical = (camera.fov * Math.PI) / 180;
	const aspect = camera.aspect || 1;
	const fovHorizontal = 2 * Math.atan(Math.tan(fovVertical / 2) * aspect);

	// Fit the footprint (a horizontal extent) to the horizontal FOV and the
	// height to the vertical FOV, then keep whichever needs the most distance.
	const footprint = Math.max(width, depth);
	const distanceForWidth = (footprint / 2) / Math.tan(fovHorizontal / 2);
	const distanceForHeight = (height / 2) / Math.tan(fovVertical / 2);
	const distance = Math.max(distanceForWidth, distanceForHeight) * FRAMING_MARGIN;

	// Store optimal distance
	self.setOptimalDistanceValue(distance);

	// Apply zoom factor
	const factor = zoom / 100;
	const targetDistance = Math.round((1 / factor) * distance);

	// Point of interest. Without an explicit centre the object is assumed to
	// stand on the ground plane at the origin, so its middle is half its height
	// up — aiming at the ground instead leaves the object sitting on the
	// viewport's centreline with empty space below it.
	const center = framing?.center ?? { x: 0, y: height / 2, z: 0 };

	// Apply camera offset
	const offsetX = offset?.x ? -offset.x * width : 0;
	const offsetY = offset?.y ? offset.y * height : 0;

	// Set camera position and target
	controls.setLookAt(
		center.x + offsetX,
		center.y + targetDistance * 0.3 + offsetY,
		center.z + targetDistance,
		center.x + offsetX,
		center.y + offsetY,
		center.z,
		framing?.animate ?? true
	);

	camera.updateProjectionMatrix();
};

export interface SetZoomParams {
	zoom: number;
}

/**
 * Set zoom level
 */
export const setZoomCfn = (
	self: CameraManager,
	{ zoom }: SetZoomParams
): void => {
	if (zoom > 2000) {
		return;
	}

	const factor = zoom / 100;
	const targetDistance = Math.round((1 / factor) * self.optimalDistance);
	self.controls.dollyTo(targetDistance, true);
	self.camera.updateProjectionMatrix();
};

export interface RotateToAngleParams {
	azimuthAngle: number;
	polarAngle?: number;
	duration?: number;
}

/**
 * Rotate camera to a specific angle
 */
export const rotateToAngleCfn = async (
	self: CameraManager,
	{ azimuthAngle, polarAngle, duration = 1.0 }: RotateToAngleParams
): Promise<void> => {
	const controls = self.controls;
	const azimuthRad = (azimuthAngle * Math.PI) / 180;

	if (polarAngle !== undefined) {
		const polarRad = (polarAngle * Math.PI) / 180;
		await controls.rotateTo(azimuthRad, polarRad, true);
	} else {
		await controls.rotateAzimuthTo(azimuthRad, true);
	}
};

export interface ZoomToFitParams {
	min: Vector3;
	max: Vector3;
	animate?: boolean;
	paddingFactor?: number;
}

/**
 * Zoom to fit a bounding box
 */
export const zoomToFitCfn = (
	self: CameraManager,
	{ min, max, animate = true, paddingFactor = 1.2 }: ZoomToFitParams
): void => {
	const camera = self.camera;
	const controls = self.controls;

	const center = new Vector3().addVectors(min, max).multiplyScalar(0.5);
	const size = new Vector3().subVectors(max, min);
	const maxDim = Math.max(size.x, size.y, size.z) * paddingFactor;

	const distance = maxDim / (2 * Math.tan((camera.fov * Math.PI) / 360));

	controls.setLookAt(
		center.x,
		center.y + distance * 0.2,
		center.z + distance,
		center.x,
		center.y,
		center.z,
		animate
	);
};
